// ─── DATE FORMATTERS ──────────────────────────────────────────────────────────
export const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-MA', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-MA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const formatTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('fr-MA', { hour: '2-digit', minute: '2-digit' });
};

export const timeAgo = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Il y a ${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  return formatDate(d);
};

// ─── NUMBER FORMATTERS ────────────────────────────────────────────────────────
export const formatPrice = (v) => {
  if (v == null) return '—';
  return `${Number(v).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
};

export const formatNumber = (v) => {
  if (v == null) return '—';
  return Number(v).toLocaleString('fr-MA');
};

export const formatPercent = (v, total) => {
  if (!total) return '0%';
  return `${Math.round((v / total) * 100)}%`;
};

// ─── STRING HELPERS ───────────────────────────────────────────────────────────
export const initials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

export const truncate = (str, len = 40) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
};

export const slugify = (str) => str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

// ─── STATUS CONFIGS ───────────────────────────────────────────────────────────
export const ORDER_STATUSES = {
  pending:            { label: 'En attente',       cls: 'badge-yellow', step: 0 },
  laverie_assigned:   { label: 'Laverie assignée', cls: 'badge-blue',   step: 1 },
  laverie_confirmed:  { label: 'Confirmée',        cls: 'badge-blue',   step: 2 },
  picked_up:          { label: 'Récupérée',        cls: 'badge-blue',   step: 3 },
  at_laverie:         { label: 'En laverie',       cls: 'badge-purple', step: 4 },
  in_progress:        { label: 'En traitement',    cls: 'badge-yellow', step: 5 },
  ready:              { label: 'Prête',            cls: 'badge-green',  step: 6 },
  delivery_assigned:  { label: 'Livreur assigné',  cls: 'badge-blue',   step: 7 },
  out_for_delivery:   { label: 'En livraison',     cls: 'badge-blue',   step: 8 },
  delivered:          { label: 'Livrée',           cls: 'badge-green',  step: 9 },
  completed:          { label: 'Terminée',         cls: 'badge-green',  step: 10 },
  cancelled:          { label: 'Annulée',          cls: 'badge-red',    step: -1 },
};

export const WORKFLOW_STEPS = [
  { key: 'pending',           label: 'Créée',      icon: '📋' },
  { key: 'laverie_assigned',  label: 'Assignée',   icon: '🏪' },
  { key: 'laverie_confirmed', label: 'Confirmée',  icon: '✅' },
  { key: 'picked_up',         label: 'Collectée',  icon: '🛵' },
  { key: 'at_laverie',        label: 'Laverie',    icon: '🧺' },
  { key: 'in_progress',       label: 'Traitement', icon: '⚙️' },
  { key: 'ready',             label: 'Prête',      icon: '📦' },
  { key: 'out_for_delivery',  label: 'Livraison',  icon: '🚗' },
  { key: 'delivered',         label: 'Livrée',     icon: '🏠' },
  { key: 'completed',         label: 'Terminée',   icon: '⭐' },
];

export const USER_STATUS = {
  active:   { label: 'Actif',    cls: 'badge-green' },
  inactive: { label: 'Inactif', cls: 'badge-red' },
};

export const CATEGORY_COLORS = {
  A: 'badge-green',
  B: 'badge-blue',
  C: 'badge-gray',
};

export const PAYMENT_LABELS = {
  cash:   { label: 'Espèces',    cls: 'badge-orange' },
  online: { label: 'En ligne',   cls: 'badge-blue' },
  points: { label: 'Points',     cls: 'badge-yellow' },
};

// ─── AVATAR COLORS ────────────────────────────────────────────────────────────
const AVATAR_PALETTES = [
  { bg: 'rgba(74,240,160,0.15)', color: '#4af0a0' },
  { bg: 'rgba(74,150,240,0.15)', color: '#4a96f0' },
  { bg: 'rgba(196,122,240,0.15)', color: '#c47af0' },
  { bg: 'rgba(240,160,74,0.15)', color: '#f0a04a' },
  { bg: 'rgba(232,240,74,0.15)', color: '#e8f04a' },
];

export const avatarStyle = (name) => {
  const idx = (name?.charCodeAt(0) || 0) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[idx];
};

// ─── PAGINATION ───────────────────────────────────────────────────────────────
export const paginate = (items, page, perPage = 15) => {
  const total = items.length;
  const totalPages = Math.ceil(total / perPage);
  const start = (page - 1) * perPage;
  return {
    items: items.slice(start, start + perPage),
    total,
    totalPages,
    page,
    perPage,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
};
